import type {OperationsJob} from './companyOperationsApi';

export type NavigationView='open'|'review'|'urgent'|'new'|'unassigned'|'active'|'affidavits'|'completed'|'exceptions'|'all'|'servers';
export type OperationsView=NavigationView|'new_today'|'due_week'|'overdue'|'completed_week';
export const operationsViews:Record<NavigationView,string>={open:'Active files',review:'Attempt review',urgent:'Urgent & unassigned',new:'New work',unassigned:'Unassigned',active:'Assigned workload',affidavits:'Affidavits',completed:'Completed / closed',exceptions:'Exceptions',all:'Search & history',servers:'Server roster'};
export const queueLabels:Record<OperationsView,string>={...operationsViews,new_today:'New files today',due_week:'Due this week',overdue:'Overdue',completed_week:'Completed this week'};
export const isClosedJob=(j:OperationsJob)=>['operationally_complete','cancelled','exception_closed'].includes(j.disposition_state);

// All date cards use the displayed workspace timezone. Stored audit timestamps are UTC.
function day(value:string,timeZone:string):string|null {
  const normalized=value.replace(' ','T');
  const instant=new Date(/(?:Z|[+-]\d\d:\d\d)$/.test(normalized)?normalized:normalized+'Z');
  if(!Number.isFinite(instant.getTime()))return null;
  const parts=new Intl.DateTimeFormat('en-US',{timeZone,year:'numeric',month:'2-digit',day:'2-digit'}).formatToParts(instant);
  return ['year','month','day'].map(type=>parts.find(p=>p.type===type)!.value).join('-');
}

/** Lists and their cards come from the same selections, before a user's search filter. */
export function buildOperationsQueues(jobs:OperationsJob[],asOf:string,timeZone:string){
  const today=day(asOf,timeZone);
  if(!today)throw new Error('Company snapshot date is unavailable.');
  const endDate=new Date(today+'T12:00:00Z');
  endDate.setUTCDate(endDate.getUTCDate()+(7-endDate.getUTCDay())%7);
  const end=endDate.toISOString().slice(0,10);
  endDate.setUTCDate(endDate.getUTCDate()-6);const start=endDate.toISOString().slice(0,10);
  const open=jobs.filter(j=>!isClosedJob(j));
  const completedDate=(j:OperationsJob)=>j.completed_at?day(j.completed_at,timeZone):null;
  const due=(j:OperationsJob)=>/^\d{4}-\d{2}-\d{2}$/.test(j.last_day_to_serve);
  const queues:Record<OperationsView,OperationsJob[]>={
    open:[...open],
    review:open.filter(j=>(j.pending_attempt_count??0)>0),
    urgent:open.filter(j=>!j.server_identity_id||(due(j)&&j.last_day_to_serve<=end)),
    new:open.filter(j=>['draft','awaiting_review'].includes(j.intake_state)),
    unassigned:open.filter(j=>!j.server_identity_id),
    active:open.filter(j=>!!j.server_identity_id),
    affidavits:open.filter(j=>(j.accepted_attempt_count??0)>0||j.affidavit_state!=='not_started'||['served','non_service','awaiting_company_acceptance','accepted'].includes(j.disposition_state)),
    completed:jobs.filter(isClosedJob),
    exceptions:jobs.filter(j=>j.disposition_state==='exception_closed'||(!isClosedJob(j)&&(
      ['changes_requested','declined'].includes(j.intake_state)||j.disposition_state==='correction_requested'||
      j.affidavit_state==='changes_requested'||j.delivery_state==='delivery_failed'||(j.correction_attempt_count??0)>0||
      (!j.server_identity_id&&j.latest_assignment_state==='declined')))),
    all:[...jobs],servers:[],
    new_today:jobs.filter(j=>day(j.file_created_at,timeZone)===today),
    due_week:open.filter(j=>due(j)&&j.last_day_to_serve>=today&&j.last_day_to_serve<=end),
    overdue:open.filter(j=>due(j)&&j.last_day_to_serve<today),
    completed_week:jobs.filter(j=>{const date=completedDate(j);return j.disposition_state==='operationally_complete'&&date!==null&&date>=start&&date<=today;}),
  };
  for(const rows of Object.values(queues))rows.sort((a,b)=>a.last_day_to_serve.localeCompare(b.last_day_to_serve)||a.job_number.localeCompare(b.job_number));
  const summary:[string,number|'Unavailable',OperationsView][]=[
    ['Active files',new Set(queues.open.map(j=>j.company_file_id)).size,'open'],
    ['Attempts to review',queues.review.reduce((sum,j)=>sum+(j.pending_attempt_count??0),0),'review'],
    ['New files today',new Set(queues.new_today.map(j=>j.company_file_id)).size,'new_today'],
    ['Unassigned',queues.unassigned.length,'unassigned'],['Due this week',queues.due_week.length,'due_week'],
    ['Overdue',queues.overdue.length,'overdue'],['Affidavits pending',queues.affidavits.length,'affidavits'],
    ['Completed this week',jobs.some(j=>j.disposition_state==='operationally_complete'&&!completedDate(j))?'Unavailable':queues.completed_week.length,'completed_week'],
  ];
  return {queues,summary,today,start,end};
}
